import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Send, RefreshCw, Clock, Eye, X, Building2,
  CheckCircle, XCircle, AlertTriangle, DollarSign, Archive,
  Calendar, Camera, Download, RotateCcw, Edit3, BarChart3,
  TrendingUp, Users, ShieldAlert, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import ClaimPreviewEdit from './ClaimPreviewEdit';
import RemittanceAdvice from './RemittanceAdvice';
import { DDEProgressTracker, AutomationActivityLog } from './AutomationActivityTracker';
import MockModeBanner from './MockModeBanner';
import FloatingNJMMISProgress from './FloatingNJMMISProgress';

const NJMMISClaimsManagement = ({ token, apiCall, sessions = [], clients = [], toast }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingAuths, setSyncingAuths] = useState(false);
  const [ddeClaims, setDDEClaims] = useState([]);
  const [eligibleSessions, setEligibleSessions] = useState([]);
  const [submittingClaimId, setSubmittingClaimId] = useState(null);
  const [updatingClaimId, setUpdatingClaimId] = useState(null);
  const [viewingClaimId, setViewingClaimId] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(false);
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);
  const [proofClaimId, setProofClaimId] = useState(null);
  const [proofData, setProofData] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [resubmitClaim, setResubmitClaim] = useState(null);
  const [resubmitForm, setResubmitForm] = useState({});
  const [resubmitting, setResubmitting] = useState(false);
  const [denialAnalytics, setDenialAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [checkingStatusId, setCheckingStatusId] = useState(null);
  const [batchStatusChecking, setBatchStatusChecking] = useState(false);
  const [statusHistoryClaimId, setStatusHistoryClaimId] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);

  const loadDDEClaims = useCallback(async () => {
    try {
      const response = await apiCall('/njmmis/live/claims', 'GET', null, token);
      const data = response?.data?.data || response?.data || [];
      setDDEClaims(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading DDE claims:', error);
      setDDEClaims([]);
    }
  }, [apiCall, token]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!isMounted) return;
      setLoading(true);
      try { await loadDDEClaims(); } catch (e) { console.error(e); }
      if (isMounted) setLoading(false);
    };
    fetchData();
    return () => { isMounted = false; };
  }, [loadDDEClaims]);

  // Auto-poll every 10s when any claim is in "submitting" state
  useEffect(() => {
    const hasSubmitting = ddeClaims.some(c => c.status === 'submitting');
    if (!hasSubmitting) return;
    const interval = setInterval(() => {
      loadDDEClaims();
    }, 10000);
    return () => clearInterval(interval);
  }, [ddeClaims, loadDDEClaims]);

  const refreshData = async () => {
    setRefreshing(true);
    try { await loadDDEClaims(); } catch (e) { console.error(e); }
    setRefreshing(false);
    toast?.success('Data refreshed');
  };

  const exportFinancials = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/njmmis/live/claims/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NJMMIS_Claims_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast?.success('Financials exported');
    } catch (e) {
      toast?.error('Failed to export financials');
    }
  };

  const [activeTab, setActiveTab] = useState('active');

  // Status check handlers
  // Poll the upload_progress endpoint for a single claim until it reports
  // `complete` or `error` (or hits a timeout), then refresh the list. Replaces
  // the previous brittle 15s setTimeout that was racing the ~30s scrape.
  const pollClaimStatusUntilDone = useCallback(async (claimId, timeoutMs = 90000) => {
    const url = `/njmmis/live/claims/status-progress/${claimId}`;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        const r = await apiCall(url, 'GET', null, token);
        const data = r?.data?.data || {};
        if (data.status === 'complete' || data.status === 'error') return data.status;
      } catch { /* swallow & retry */ }
      // 2.5s between polls
  
      await new Promise((res) => setTimeout(res, 2500));
    }
    return 'timeout';
    // apiCall and token are stable refs from props
  // apiCall is module-stable (imported from utils/api).
  // eslint-disable-next-line
  }, [token]);

  const checkClaimStatus = async (claimId) => {
    try {
      setCheckingStatusId(claimId);
      const response = await apiCall(`/njmmis/live/claims/status-check/${claimId}`, 'POST', {}, token);
      const data = response?.data;
      if (data?.success) {
        toast?.success(data?.data?.message || 'Status check started — refreshing when done');
        // Poll progress until done, then refresh the claims list. Falls back
        // to a 30s refresh if polling times out (matches old behavior).
        const finalStatus = await pollClaimStatusUntilDone(claimId);
        await loadDDEClaims();
        if (finalStatus === 'complete') {
          toast?.success('Claim status updated');
        } else if (finalStatus === 'error') {
          toast?.error('Status check returned an error — view history for details');
        }
      } else {
        toast?.error('Status check failed');
      }
    } catch (e) {
      toast?.error('Error checking claim status');
    } finally {
      setCheckingStatusId(null);
    }
  };

  const batchCheckStatus = async () => {
    try {
      setBatchStatusChecking(true);
      const claimIds = submittedClaims.map(c => c.claim_id);
      const response = await apiCall('/njmmis/live/claims/batch-status-check', 'POST', { claim_ids: claimIds }, token);
      if (response?.data?.success) {
        toast?.success(`Status check started for ${response.data.claims_to_check} claims`);
        // Poll the progress endpoint for each claim sequentially, refreshing
        // the list every time one completes so the user sees rolling updates
        // instead of a long silent wait followed by a single refresh.
        let completed = 0;
        const total = claimIds.length;
        for (const cid of claimIds) {
      
          await pollClaimStatusUntilDone(cid, 60000);
          completed += 1;
          // Refresh after each claim so the cards update in real-time.
          // Skip the very last refresh — the loop exit will handle it.
          if (completed < total) {
        
            await loadDDEClaims();
          }
        }
        await loadDDEClaims();
        toast?.success(`Batch status check complete (${completed}/${total})`);
      }
    } catch (e) {
      toast?.error('Error starting batch status check');
    } finally {
      setBatchStatusChecking(false);
    }
  };

  const viewStatusHistory = async (claimId) => {
    try {
      const response = await apiCall(`/njmmis/live/claims/${claimId}/status-history`, 'GET', null, token);
      if (response?.data?.success) {
        setStatusHistory(response.data.data?.status_history || []);
        setStatusHistoryClaimId(claimId);
      }
    } catch (e) {
      toast?.error('Error loading status history');
    }
  };

  // Active = generated, failed (need action)
  const activeClaims = ddeClaims.filter(c => ['generated', 'failed', 'submitting'].includes(c.status));
  // Submitted = awaiting payment
  const submittedClaims = ddeClaims.filter(c => c.status === 'submitted');
  // Archived = paid, archived, denied, adjusted
  const archivedClaims = ddeClaims.filter(c => ['paid', 'archived', 'denied', 'adjusted', 'accepted'].includes(c.status));

  const stats = {
    total: ddeClaims.length,
    generated: ddeClaims.filter(c => c.status === 'generated').length,
    submitted: ddeClaims.filter(c => c.status === 'submitted').length,
    paid: ddeClaims.filter(c => c.status === 'paid' || c.status === 'archived').length,
    denied: ddeClaims.filter(c => c.status === 'denied' || c.status === 'adjusted').length,
    failed: ddeClaims.filter(c => c.status === 'failed').length,
    totalBilled: ddeClaims.reduce((sum, c) => sum + (c.total_charge || 0), 0),
    totalPaid: ddeClaims.filter(c => c.status === 'paid' || c.status === 'archived').reduce((sum, c) => sum + (c.paid_amount || c.total_charge || 0), 0),
  };

  const updateClaimStatus = async (claimId, newStatus, extra = {}) => {
    setUpdatingClaimId(claimId);
    try {
      const response = await apiCall(`/njmmis/live/claims/${claimId}/status`, 'PATCH', {
        status: newStatus, ...extra
      }, token);
      if (response?.data?.success) {
        toast?.success(`Claim marked as ${newStatus}`);
        await loadDDEClaims();
      } else {
        toast?.error('Failed to update claim status');
      }
    } catch (error) {
      console.error('Error updating claim status:', error);
      toast?.error('Failed to update claim status');
    }
    setUpdatingClaimId(null);
  };

  const deleteClaim = async (claimId) => {
    if (!window.confirm('Permanently delete this claim? This cannot be undone.')) return;
    setUpdatingClaimId(claimId);
    try {
      const response = await apiCall(`/njmmis/live/claims/${claimId}`, 'DELETE', null, token);
      if (response?.data?.success) {
        toast?.success('Claim deleted');
        await loadDDEClaims();
      } else {
        toast?.error('Failed to delete claim');
      }
    } catch (error) {
      toast?.error('Failed to delete claim');
    }
    setUpdatingClaimId(null);
  };

  const handleBulkAction = async (action) => {
    if (selectedClaimIds.length === 0) {
      toast?.error('Select claims first');
      return;
    }
    
    // For 'submitted' action, run actual DDE submissions sequentially
    if (action === 'submitted') {
      // Filter to only generated claims (skip already submitted/paid)
      const generatedIds = selectedClaimIds.filter(id => {
        const claim = ddeClaims.find(c => c.claim_id === id);
        return claim?.status === 'generated';
      });
      const skippedFailed = selectedClaimIds.filter(id => {
        const claim = ddeClaims.find(c => c.claim_id === id);
        return claim?.status === 'failed';
      }).length;
      if (generatedIds.length === 0) {
        const msg = skippedFailed > 0
          ? `${skippedFailed} failed claim(s) skipped — review and retry individually`
          : 'No claims ready for DDE submission';
        toast?.info(msg);
        return;
      }
      if (skippedFailed > 0) {
        toast?.info(`${skippedFailed} failed claim(s) skipped — retry individually after review`);
      }
      setLoading(true);
      let successCount = 0;
      let failCount = 0;
      for (const claimId of generatedIds) {
        setSubmittingClaimId(claimId);
        try {
          const response = await apiCall('/njmmis/live/claims/submit-dde', 'POST', { claim_id: claimId }, token);
          // Same unwrap fix as handleSubmitDDE — read the outer apiCall
          // success flag, not the inner nested data object.
          const dispatched = response?.success && (response?.data?.success !== false);
          if (dispatched) {
            successCount++;
          } else {
            failCount++;
            toast?.error(`Failed: ${response?.data?.error || response?.error || 'Unknown error'}`);
          }
        } catch (error) {
          failCount++;
          toast?.error(`Failed to submit claim ${claimId.slice(0, 8)}`);
        }
      }
      setSubmittingClaimId(null);
      if (successCount > 0) {
        toast?.success(`Bulk DDE: ${successCount} claim(s) dispatched for submission${failCount > 0 ? `, ${failCount} failed to dispatch` : ''}`);
      }
      setSelectedClaimIds([]);
      setBulkAction(false);
      await loadDDEClaims();
      setLoading(false);
      return;
    }
    
    // For 'delete' action, use the bulk-delete endpoint
    if (action === 'delete') {
      if (!window.confirm(`Permanently delete ${selectedClaimIds.length} claim(s)? This cannot be undone.`)) return;
      setLoading(true);
      try {
        const response = await apiCall('/njmmis/live/claims/bulk-delete', 'POST', {
          claim_ids: selectedClaimIds
        }, token);
        const data = response?.data?.data || response?.data;
        if (data?.deleted > 0) {
          toast?.success(`${data.deleted} claim(s) deleted`);
          setSelectedClaimIds([]);
          setBulkAction(false);
          await loadDDEClaims();
        }
      } catch (error) {
        toast?.error('Bulk delete failed');
      }
      setLoading(false);
      return;
    }
    
    // For other actions (paid, archived), use the bulk-status endpoint
    setLoading(true);
    try {
      const response = await apiCall('/njmmis/live/claims/bulk-status', 'POST', {
        claim_ids: selectedClaimIds,
        status: action
      }, token);
      const data = response?.data?.data || response?.data;
      if (data?.updated > 0) {
        toast?.success(`${data.updated} claim(s) marked as ${action}`);
        setSelectedClaimIds([]);
        setBulkAction(false);
        await loadDDEClaims();
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      toast?.error('Bulk action failed');
    }
    setLoading(false);
  };

  const handleSubmitDDE = async (claimId) => {
    setSubmittingClaimId(claimId);
    try {
      const response = await apiCall('/njmmis/live/claims/submit-dde', 'POST', { claim_id: claimId }, token);
      // ``apiCall`` returns {success, data: <backend body>}. The
      // backend body is {success, status:"submitting", data:{...}}.
      // Previously we collapsed to ``response.data.data`` which
      // dropped the success flag and made every successful dispatch
      // flash a red "DDE submission failed" toast for a beat before
      // the live tracker took over — fixed 2026-05-30.
      const dispatched = response?.success && (response?.data?.success !== false);
      if (dispatched) {
        toast?.success(response?.data?.data?.message || 'DDE submission started — claim will update automatically');
        await loadDDEClaims();
      } else {
        const err = response?.data?.error || response?.error || 'DDE submission failed';
        toast?.error(err);
      }
    } catch (error) {
      toast?.error('Failed to submit claim via DDE');
    }
    setSubmittingClaimId(null);
  };

  const handleAutoGenerate = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/njmmis/live/claims/generate', 'POST', { auto_detect: true }, token);
      const data = response?.data?.data || response?.data || response;
      if (data?.total_generated > 0) {
        toast?.success(`Auto-generated ${data.total_generated} CMS-1500 claim(s)`);
        // Show auth warnings if any
        const warnings = data?.auth_warnings || [];
        if (warnings.length > 0) {
          const names = warnings.map(w => w.patient_name).join(', ');
          toast?.warning(`Authorization warnings for: ${names}. Check auth status before submitting.`, { duration: 8000 });
        }
        await loadDDEClaims();
      } else {
        toast?.info('No eligible sessions found for claim generation');
      }
    } catch (error) {
      toast?.error('Failed to auto-generate claims');
    }
    setLoading(false);
  };

  // iter-123 — Split-per-DOS retro action (one-click backfill).
  // Fires the new backend endpoint; dry-runs first so we can show a
  // confirm dialog with the exact count before mutating.
  const handleSplitPerDOS = async () => {
    setLoading(true);
    try {
      const preview = await apiCall(
        '/njmmis/live/claims/split-bulk-into-per-dos?dry_run=true',
        'POST', null, token
      );
      const p = preview?.data?.data || preview?.data || preview;
      const bulk = p?.bulk_claims_found || 0;
      const estimated = p?.estimated_new_claims || 0;
      if (bulk === 0) {
        toast?.info('No bulk claims to split — every ready-to-submit claim is already per-DOS.');
        setLoading(false);
        return;
      }
      const proceed = window.confirm(
        `Split ${bulk} bulk claim${bulk !== 1 ? 's' : ''} into ~${estimated} per-date-of-service claim${estimated !== 1 ? 's' : ''}?\n\n` +
        `Each existing multi-date bulk row will be deleted and regenerated as one claim per session date. ` +
        `Underlying sessions are preserved. This action is audited.`
      );
      if (!proceed) { setLoading(false); return; }

      const apply = await apiCall(
        '/njmmis/live/claims/split-bulk-into-per-dos?dry_run=false',
        'POST', null, token
      );
      const a = apply?.data?.data || apply?.data || apply;
      const split = a?.bulk_claims_split || 0;
      const newN = a?.new_per_dos_claims || 0;
      const errs = (a?.errors || []).length;
      if (errs) {
        toast?.warning(`Split ${split} bulk → ${newN} per-DOS claims, but ${errs} error(s). Check server logs.`, { duration: 8000 });
      } else {
        toast?.success(`Split ${split} bulk claim(s) into ${newN} per-DOS claims.`);
      }
      await loadDDEClaims();
    } catch (error) {
      toast?.error('Failed to split bulk claims into per-DOS');
    }
    setLoading(false);
  };



  const handleSyncAuths = async () => {
    setSyncingAuths(true);
    try {
      const response = await apiCall('/performcare/authorizations/import', 'POST', { use_live: true }, token);
      const data = response?.data?.data || response?.data || response;
      if (data?.success !== false) {
        toast?.success(data?.message || `Authorization sync started for ${data?.total_clients || 'all'} clients`);
      } else {
        toast?.error('Failed to start authorization sync');
      }
    } catch (error) {
      toast?.error('Failed to sync authorizations from CYBER');
    }
    setSyncingAuths(false);
  };

  const handleRetryAllFailed = async () => {
    const failedClaims = ddeClaims.filter(c => c.status === 'failed');
    if (!failedClaims.length) {
      toast?.info('No failed claims to retry');
      return;
    }
    setLoading(true);
    let dispatched = 0;
    // iter-48: send batch_index (1-based) + batch_total on each
    // dispatch so the FloatingNJMMISProgress tracker can render
    // "Claim 3 of 42 · Youth AI" during a Retry-All-Failed run.
    const batchTotal = failedClaims.length;
    for (let i = 0; i < failedClaims.length; i++) {
      const claim = failedClaims[i];
      try {
        setSubmittingClaimId(claim.claim_id);
        const response = await apiCall(
          '/njmmis/live/claims/submit-dde',
          'POST',
          {
            claim_id: claim.claim_id,
            batch_index: i + 1,
            batch_total: batchTotal,
          },
          token,
        );
        if (response?.data?.success) dispatched++;
      } catch {
        // continue with next
      }
    }
    setSubmittingClaimId(null);
    if (dispatched > 0) {
      toast?.success(`Dispatched ${dispatched}/${failedClaims.length} failed claims for retry`);
      await loadDDEClaims();
    }
    setLoading(false);
  };

  const handleViewProof = async (claimId) => {
    setProofClaimId(claimId);
    setLoadingProof(true);
    try {
      const response = await apiCall(`/njmmis/live/claims/dde/${claimId}/evidence`, 'GET', null, token);
      const data = response?.data?.evidence || response?.data || [];
      setProofData(Array.isArray(data) ? data : [data]);
    } catch (error) {
      toast?.error('Failed to load submission proof');
      setProofData([]);
    }
    setLoadingProof(false);
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const loadDenialAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const response = await apiCall('/njmmis/live/claims/denial-analytics', 'GET', null, token);
      const data = response?.data || response;
      if (data?.success) {
        setDenialAnalytics(data.data);
      }
    } catch (error) {
      console.error('Failed to load denial analytics:', error);
    }
    setLoadingAnalytics(false);
  }, [apiCall, token]);

  const openResubmitModal = (claim) => {
    setResubmitClaim(claim);
    setResubmitForm({
      prior_auth_number: claim.prior_auth_number || '',
      diagnosis_codes: (claim.diagnosis_codes || []).join(', '),
      reason: '',
      service_lines: (claim.service_lines || []).map(sl => ({
        ...sl,
        modifiers: (sl.modifiers || []).join(' '),
      })),
    });
  };

  const handleResubmit = async () => {
    if (!resubmitClaim) return;
    setResubmitting(true);
    try {
      const payload = {
        reason: resubmitForm.reason,
      };
      if (resubmitForm.prior_auth_number !== (resubmitClaim.prior_auth_number || '')) {
        payload.prior_auth_number = resubmitForm.prior_auth_number;
      }
      const newDx = resubmitForm.diagnosis_codes.split(',').map(s => s.trim()).filter(Boolean);
      const origDx = (resubmitClaim.diagnosis_codes || []);
      if (JSON.stringify(newDx) !== JSON.stringify(origDx)) {
        payload.diagnosis_codes = newDx;
      }
      // Check for service line overrides
      const slOverrides = {};
      (resubmitForm.service_lines || []).forEach((sl, idx) => {
        const origSl = resubmitClaim.service_lines?.[idx] || {};
        const newMods = sl.modifiers.split(/[\s,]+/).filter(Boolean);
        const origMods = origSl.modifiers || [];
        const changes = {};
        if (JSON.stringify(newMods) !== JSON.stringify(origMods)) changes.modifiers = newMods;
        if (Number(sl.units) !== Number(origSl.units)) changes.units = Number(sl.units);
        if (Number(sl.charges) !== Number(origSl.charges)) changes.charges = Number(sl.charges);
        if (Object.keys(changes).length > 0) slOverrides[String(idx)] = changes;
      });
      if (Object.keys(slOverrides).length > 0) payload.service_line_overrides = slOverrides;

      const response = await apiCall(`/njmmis/live/claims/${resubmitClaim.claim_id}/resubmit`, 'POST', payload, token);
      const data = response?.data?.data || response?.data || response;
      if (data?.success) {
        toast?.success(`Corrected claim created — check Active tab`);
        setResubmitClaim(null);
        setResubmitForm({});
        setActiveTab('active');
        await loadDDEClaims();
      } else {
        toast?.error(data?.detail || 'Resubmission failed');
      }
    } catch (error) {
      toast?.error(error?.response?.data?.detail || 'Failed to create resubmission');
    }
    setResubmitting(false);
  };

  const loadEligibleSessions = async () => {
    try {
      const response = await apiCall('/njmmis/live/claims/eligible-sessions', 'GET', null, token);
      const data = response?.data?.data || response?.data || [];
      setEligibleSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading eligible sessions:', error);
      setEligibleSessions([]);
    }
  };

  const handleGenerateClaims = async () => {
    if (selectedSessionIds.length === 0) { toast?.error('Select at least one session'); return; }
    setLoading(true);
    try {
      const response = await apiCall('/njmmis/live/claims/generate', 'POST', { session_ids: selectedSessionIds }, token);
      const data = response?.data?.data || response?.data || response;
      if (data?.total_generated > 0) {
        toast?.success(`Generated ${data.total_generated} CMS-1500 claim(s)`);
        setShowGenerateModal(false);
        setSelectedSessionIds([]);
        await loadDDEClaims();
      } else if (data?.total_errors > 0) {
        toast?.error(`Failed: ${data?.claims?.find(c => c.error)?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast?.error('Failed to generate claims');
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const cfg = {
      generated: { color: 'bg-gray-100 text-gray-800 border-gray-300', icon: FileText, label: 'Generated' },
      submitting: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: RefreshCw, label: 'Submitting...' },
      submitted: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Send, label: 'Submitted' },
      accepted: { color: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: CheckCircle, label: 'Accepted' },
      paid: { color: 'bg-green-100 text-green-800 border-green-300', icon: DollarSign, label: 'Paid' },
      denied: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Denied' },
      failed: { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle, label: 'Failed' },
      adjusted: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertTriangle, label: 'Adjusted' },
      archived: { color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Archive, label: 'Archived (Paid)' },
    };
    const c = cfg[status] || cfg.generated;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />{c.label}
      </Badge>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  const ClaimRow = ({ claim, showActions = true, showBulk = false }) => (
    <div
      key={claim.claim_id}
      className="px-4 py-3 hover:bg-gray-50 transition-colors"
      data-testid={`dde-claim-${claim.claim_id}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          {(bulkAction && showActions || showBulk) && (
            <input
              type="checkbox"
              className="mr-2 w-4 h-4 align-middle"
              checked={selectedClaimIds.includes(claim.claim_id)}
              onChange={() => {
                setSelectedClaimIds(prev =>
                  prev.includes(claim.claim_id)
                    ? prev.filter(id => id !== claim.claim_id)
                    : [...prev, claim.claim_id]
                );
              }}
            />
          )}
          <div className="flex items-center gap-2 mb-1 inline-flex flex-wrap">
            <span className="font-semibold text-sm">
              {claim.patient_first_name} {claim.patient_last_name}
            </span>
            {getStatusBadge(claim.status)}
            {/* iter-127 — Per-DOS badge (split-lineage indicator).
                Claims created via the iter-123 per-date-of-service flow
                (auto-generate, manual generate, or retro split) carry
                ``billing_grouping: "per_date_of_service"`` and a
                ``service_date``. Rendering a compact date pill next to
                the name gives billers instant visual confirmation
                that this row is 1:1 with a session — not a bundled
                multi-week claim like the pre-iter-123 world. */}
            {claim.billing_grouping === 'per_date_of_service' && claim.service_date && (
              <Badge
                className="bg-indigo-50 text-indigo-700 border-indigo-200 border text-xs"
                title="One claim per date of service (iter-123). Split from bulk if applicable."
                data-testid={`per-dos-badge-${claim.claim_id}`}
              >
                DOS: {String(claim.service_date).slice(0, 10)}
              </Badge>
            )}
            {claim.icn && (
              <Badge className="bg-green-50 text-green-700 border-green-200 border text-xs">
                ICN: {claim.icn}
              </Badge>
            )}
            {/* Auth warnings only matter for pre-submission triage.
                For claims already paid/denied/archived, NJMMIS has rendered its
                verdict — showing "Auth Exhausted" next to a paid claim is noise.

                ``auth_warning_live`` is the backend's freshly-recomputed
                value (against the latest synced CYBER auths). Prefer it
                when present so stale snapshots from claim-generation
                time don't keep showing a wrong "No Active Auth" badge
                after CYBER finally syncs the matching authorization.
                The stored ``auth_warning`` stays in DB for audit. */}
            {(() => {
              const liveWarn = claim.auth_warning_live !== undefined
                ? claim.auth_warning_live
                : claim.auth_warning;
              if (!liveWarn) return null;
              if (['paid', 'denied', 'archived'].includes(claim.status)) return null;
              return (
                <Badge className="bg-amber-50 text-amber-800 border-amber-300 border text-xs" data-testid={`auth-warning-${claim.claim_id}`}>
                  <AlertTriangle className="w-3 h-3 mr-1 inline" />
                  {liveWarn === 'authorization_expired' ? 'Auth Expired'
                   : liveWarn === 'authorization_exhausted' ? 'Auth Exhausted'
                   : liveWarn === 'no_authorizations' ? 'No Auth'
                   : liveWarn === 'no_active_authorization' ? 'No Active Auth'
                   : 'Auth Issue'}
                </Badge>
              );
            })()}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>Medicaid: <span className="font-mono">{claim.medicaid_id}</span></span>
            <span>Auth: <span className="font-mono">{claim.prior_auth_number || '—'}</span></span>
            <span>Dx: <span className="font-mono">{(claim.diagnosis_codes || []).join(', ')}</span></span>
            <span className="font-semibold text-gray-700">${(claim.total_charge || 0).toFixed(2)}</span>
          </div>
          {/* Timestamps row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
            {claim.submitted_at && (
              <span className="text-blue-600 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Submitted: {formatDate(claim.submitted_at)}
              </span>
            )}
            {claim.paid_at && claim.status !== 'denied' && (
              <span className="text-green-600 flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> Paid: {formatDate(claim.paid_at)}
                {claim.paid_amount != null && ` ($${claim.paid_amount.toFixed(2)})`}
              </span>
            )}
            {claim.archived_at && (
              <span className="text-slate-500 flex items-center gap-1">
                <Archive className="w-3 h-3" /> Archived: {formatDate(claim.archived_at)}
              </span>
            )}
          </div>
          {claim.service_lines?.map((sl, idx) => (
            <div key={idx} className="mt-1 text-xs text-gray-400 font-mono">
              {sl.date_from} | POS {sl.place_of_service} | {sl.cpt_code} {sl.modifiers?.join(' ')} | {sl.units}u | ${(sl.charges || 0).toFixed(2)}
            </div>
          ))}
          {claim.submission_error && claim.status === 'failed' && (
            <div className="mt-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{claim.submission_error}</div>
          )}
          {claim.denial_reason && (
            <div className="mt-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded flex items-center gap-1">
              <XCircle className="w-3 h-3 flex-shrink-0" /> Denial: {claim.denial_reason}
            </div>
          )}
          {claim.resubmission_of && (
            <div className="mt-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
              <RotateCcw className="w-3 h-3 flex-shrink-0" /> Resubmission of {claim.resubmission_of.slice(0, 8)}...
              {claim.resubmission_reason && <span className="text-gray-500 ml-1">({claim.resubmission_reason})</span>}
            </div>
          )}
          {claim.last_status_result && (
            <div className="mt-1 text-xs bg-gray-50 px-2 py-1 rounded flex items-center gap-2">
              <span className="text-gray-500">Last check:</span>
              <span className={`font-medium ${
                claim.last_status_result.status === 'paid' ? 'text-green-700' :
                claim.last_status_result.status === 'denied' ? 'text-red-700' :
                claim.last_status_result.status === 'pending' ? 'text-amber-700' :
                'text-gray-700'
              }`}>
                {claim.last_status_result.status}
              </span>
              {claim.last_status_result.paid_amount && (
                <span className="text-green-600">${claim.last_status_result.paid_amount.toFixed(2)}</span>
              )}
              <span className="text-gray-400">{formatDate(claim.last_status_check)}</span>
              <button
                onClick={() => viewStatusHistory(claim.claim_id)}
                className="text-blue-600 hover:underline ml-1"
                data-testid={`view-history-${claim.claim_id}`}
              >
                History
              </button>
            </div>
          )}
        </div>
        {showActions && (
          <div className="flex gap-2 ml-3 flex-shrink-0 flex-wrap justify-end">
            {/* Status progression buttons */}
            {claim.status === 'generated' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleSubmitDDE(claim.claim_id)}
                  disabled={submittingClaimId === claim.claim_id}
                  className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                  data-testid={`submit-dde-${claim.claim_id}`}
                >
                  {submittingClaimId === claim.claim_id
                    ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Submitting</>
                    : <><Send className="w-3 h-3 mr-1" /> Submit DDE</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => updateClaimStatus(claim.claim_id, 'submitted')}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`mark-submitted-${claim.claim_id}`}
                >
                  Mark Submitted
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-xs h-8"
                  onClick={() => updateClaimStatus(claim.claim_id, 'paid')}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`mark-paid-${claim.claim_id}`}
                >
                  <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => deleteClaim(claim.claim_id)}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`delete-generated-${claim.claim_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </>
            )}
            {claim.status === 'submitted' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => checkClaimStatus(claim.claim_id)}
                  disabled={checkingStatusId === claim.claim_id}
                  data-testid={`check-status-${claim.claim_id}`}
                >
                  {checkingStatusId === claim.claim_id
                    ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Checking</>
                    : <><RefreshCw className="w-3 h-3 mr-1" /> Check Status</>}
                </Button>
                {claim.evidence_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => handleViewProof(claim.claim_id)}
                    data-testid={`view-proof-${claim.claim_id}`}
                  >
                    <Camera className="w-3 h-3 mr-1" /> View Proof
                  </Button>
                )}
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-xs h-8"
                  onClick={() => updateClaimStatus(claim.claim_id, 'paid')}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`mark-paid-${claim.claim_id}`}
                >
                  <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 border-slate-300 text-slate-600"
                  onClick={() => updateClaimStatus(claim.claim_id, 'archived')}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`archive-submitted-${claim.claim_id}`}
                >
                  <Archive className="w-3 h-3 mr-1" /> Archive
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => deleteClaim(claim.claim_id)}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`delete-submitted-${claim.claim_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </>
            )}
            {(claim.status === 'paid') && (
              <>
                {claim.evidence_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => handleViewProof(claim.claim_id)}
                    data-testid={`view-proof-paid-${claim.claim_id}`}
                  >
                    <Camera className="w-3 h-3 mr-1" /> View Proof
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 border-slate-300 text-slate-600"
                  onClick={() => updateClaimStatus(claim.claim_id, 'archived')}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`archive-${claim.claim_id}`}
                >
                  <Archive className="w-3 h-3 mr-1" /> Archive
                </Button>
              </>
            )}
            {['denied', 'adjusted', 'needs_review'].includes(claim.status) && !claim.resubmitted && (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                onClick={() => openResubmitModal(claim)}
                data-testid={`resubmit-${claim.claim_id}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                {claim.status === 'needs_review' ? 'Retry as New' : 'Resubmit'}
              </Button>
            )}
            {claim.resubmitted && (
              <Badge className="bg-slate-100 text-slate-500 border-slate-200 border text-xs">
                Resubmitted
              </Badge>
            )}
            {claim.status === 'failed' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleSubmitDDE(claim.claim_id)}
                  disabled={submittingClaimId === claim.claim_id}
                  className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                  data-testid={`submit-dde-${claim.claim_id}`}
                >
                  {submittingClaimId === claim.claim_id
                    ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Submitting</>
                    : <><Send className="w-3 h-3 mr-1" /> Retry DDE</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => deleteClaim(claim.claim_id)}
                  disabled={updatingClaimId === claim.claim_id}
                  data-testid={`delete-failed-${claim.claim_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </>
            )}
            <Button
              size="sm" variant="ghost" className="text-xs h-8"
              onClick={() => setViewingClaimId(claim.claim_id)}
              data-testid={`view-claim-${claim.claim_id}`}
            >
              <Eye className="w-3 h-3 mr-1" /> Preview
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="njmmis-claims-management">
      <MockModeBanner token={token} portalName="NJMMIS" />
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                <CardTitle className="text-lg">NJMMIS Claims</CardTitle>
              </div>
              <CardDescription className="mt-1">CMS-1500 claims via NJMMIS Direct Data Entry</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportFinancials} data-testid="export-financials-btn">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
              <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleAutoGenerate} disabled={loading} data-testid="auto-generate-btn">
                {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Auto-Generate
              </Button>
              <Button variant="outline" size="sm" onClick={handleSyncAuths} disabled={syncingAuths} data-testid="sync-auths-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50">
                {syncingAuths ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync Auths
              </Button>
              {stats.failed > 0 && (
                <Button variant="outline" size="sm" onClick={handleRetryAllFailed} disabled={loading} data-testid="retry-all-failed-btn"
                  className="border-red-300 text-red-700 hover:bg-red-50">
                  {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Retry All Failed ({stats.failed})
                </Button>
              )}
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => { loadEligibleSessions(); setShowGenerateModal(true); }} data-testid="generate-claims-btn">
                <FileText className="w-4 h-4 mr-1" /> Select Sessions
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Total Claims</p>
          <p className="text-xl font-bold">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Ready to Submit</p>
          <p className="text-xl font-bold text-amber-600">{stats.generated}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Submitted</p>
          <p className="text-xl font-bold text-blue-600">{stats.submitted}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Paid</p>
          <p className="text-xl font-bold text-green-600">{stats.paid}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Denied</p>
          <p className="text-xl font-bold text-red-600" data-testid="stats-denied">{stats.denied}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs text-gray-500">Total Collected</p>
          <p className="text-xl font-bold text-green-600">${stats.totalPaid.toFixed(2)}</p>
        </CardContent></Card>
      </div>

      {/* Tabbed Claims View */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setBulkAction(false); setSelectedClaimIds([]); }}>
        <TabsList className="bg-gray-100 p-1 mb-3">
          <TabsTrigger value="active" className="data-[state=active]:bg-white" data-testid="tab-active">
            <FileText className="w-4 h-4 mr-1.5" /> Active ({activeClaims.length})
          </TabsTrigger>
          <TabsTrigger value="submitted" className="data-[state=active]:bg-white" data-testid="tab-submitted">
            <Send className="w-4 h-4 mr-1.5" /> Submitted ({submittedClaims.length})
          </TabsTrigger>
          <TabsTrigger value="archived" className="data-[state=active]:bg-white" data-testid="tab-archived">
            <Archive className="w-4 h-4 mr-1.5" /> Paid / Archived ({archivedClaims.length})
          </TabsTrigger>
          <TabsTrigger value="remittance" className="data-[state=active]:bg-white" data-testid="tab-remittance">
            <DollarSign className="w-4 h-4 mr-1.5" /> Remittance
          </TabsTrigger>
          <TabsTrigger 
            value="denial-analytics" 
            className="data-[state=active]:bg-white" 
            data-testid="tab-denial-analytics"
            onClick={() => { if (!denialAnalytics) loadDenialAnalytics(); }}
          >
            <BarChart3 className="w-4 h-4 mr-1.5" /> Denial Analytics
          </TabsTrigger>
        </TabsList>

        {/* Active Claims Tab */}
        <TabsContent value="active">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Ready to Submit ({activeClaims.length})</CardTitle>
                {activeClaims.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm"
                      className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={handleSplitPerDOS}
                      disabled={loading}
                      data-testid="split-bulk-per-dos-btn"
                      title="Break out multi-date claims into one claim per date of service"
                    >
                      Split into Per-DOS
                    </Button>
                    <Button
                      variant="outline" size="sm" className="text-xs h-7"
                      onClick={() => { setBulkAction(!bulkAction); setSelectedClaimIds([]); }}
                      data-testid="bulk-select-toggle"
                    >
                      {bulkAction ? 'Cancel' : 'Bulk Actions'}
                    </Button>
                    {bulkAction && (
                      <Button size="sm" variant="outline" className="text-xs h-7"
                        onClick={() => {
                          const selectableIds = activeClaims.filter(c => c.status !== 'submitting').map(c => c.claim_id);
                          setSelectedClaimIds(prev => prev.length === selectableIds.length ? [] : selectableIds);
                        }}
                        data-testid="bulk-select-all"
                      >
                        {selectedClaimIds.length === activeClaims.filter(c => c.status !== 'submitting').length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                    {bulkAction && selectedClaimIds.length > 0 && (
                      <>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-blue-300 text-blue-700"
                          onClick={() => handleBulkAction('submitted')} data-testid="bulk-mark-submitted">
                          <Send className="w-3 h-3 mr-1" /> Bulk Submit DDE ({selectedClaimIds.length})
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7"
                          onClick={() => handleBulkAction('paid')} data-testid="bulk-mark-paid">
                          <DollarSign className="w-3 h-3 mr-1" /> Mark Paid ({selectedClaimIds.length})
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleBulkAction('delete')} data-testid="bulk-delete-active">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete ({selectedClaimIds.length})
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading && activeClaims.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" /><p>Loading claims...</p>
                </div>
              ) : activeClaims.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-300" />
                  <p className="font-medium">No claims pending</p>
                  <p className="text-sm mt-1">Click "Select Sessions" or "Auto-Generate" to create new claims</p>
                </div>
              ) : (
                <div className="divide-y">
                  {activeClaims.map(claim => <ClaimRow key={claim.claim_id} claim={claim} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submitted Claims Tab */}
        <TabsContent value="submitted">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Submitted — Awaiting Payment ({submittedClaims.length})</CardTitle>
                {submittedClaims.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm" className="text-xs h-7 text-blue-700 border-blue-300"
                      onClick={batchCheckStatus}
                      disabled={batchStatusChecking}
                      data-testid="batch-status-check"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${batchStatusChecking ? 'animate-spin' : ''}`} />
                      {batchStatusChecking ? 'Checking...' : 'Check All Status'}
                    </Button>
                    <Button
                      variant="outline" size="sm" className="text-xs h-7"
                      onClick={() => { setBulkAction(!bulkAction); setSelectedClaimIds([]); }}
                      data-testid="bulk-select-toggle-submitted"
                    >
                      {bulkAction ? 'Cancel' : 'Bulk Actions'}
                    </Button>
                    {bulkAction && (
                      <Button size="sm" variant="outline" className="text-xs h-7"
                        onClick={() => {
                          const allIds = submittedClaims.map(c => c.claim_id);
                          setSelectedClaimIds(prev => prev.length === allIds.length ? [] : allIds);
                        }}
                        data-testid="bulk-select-all-submitted"
                      >
                        {selectedClaimIds.length === submittedClaims.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                    {bulkAction && selectedClaimIds.length > 0 && (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7"
                          onClick={() => handleBulkAction('paid')} data-testid="bulk-mark-paid-submitted">
                          <DollarSign className="w-3 h-3 mr-1" /> Mark Paid ({selectedClaimIds.length})
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-slate-300 text-slate-600"
                          onClick={() => handleBulkAction('archived')} data-testid="bulk-archive-submitted">
                          <Archive className="w-3 h-3 mr-1" /> Archive ({selectedClaimIds.length})
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleBulkAction('delete')} data-testid="bulk-delete-submitted">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete ({selectedClaimIds.length})
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {submittedClaims.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Send className="w-10 h-10 mx-auto mb-3 text-blue-200" />
                  <p className="font-medium">No submitted claims</p>
                  <p className="text-sm mt-1">Claims will appear here after DDE submission</p>
                </div>
              ) : (
                <div className="divide-y">
                  {submittedClaims.map(claim => <ClaimRow key={claim.claim_id} claim={claim} showBulk={bulkAction} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paid / Archived Tab */}
        <TabsContent value="archived">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Archive className="w-4 h-4 text-slate-500" />
                Paid / Archived ({archivedClaims.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {archivedClaims.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-10 h-10 mx-auto mb-3 text-green-200" />
                  <p className="font-medium">No paid or archived claims</p>
                  <p className="text-sm mt-1">Claims move here after being marked as paid</p>
                </div>
              ) : (
                <div className="divide-y">
                  {archivedClaims.map(claim => (
                    <ClaimRow
                      key={claim.claim_id}
                      claim={claim}
                      showActions={['denied', 'adjusted', 'paid'].includes(claim.status)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Remittance Tab */}
        <TabsContent value="remittance">
          <RemittanceAdvice token={token} />
          <div className="mt-4">
            <AutomationActivityLog token={token} system="njmmis" limit={50} />
          </div>
        </TabsContent>

        {/* Denial Analytics Tab */}
        <TabsContent value="denial-analytics">
          {loadingAnalytics ? (
            <Card><CardContent className="py-12 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">Loading denial analytics...</p>
            </CardContent></Card>
          ) : !denialAnalytics ? (
            <Card><CardContent className="py-12 text-center">
              <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">Click to load denial analytics</p>
              <Button size="sm" className="mt-3" onClick={loadDenialAnalytics} data-testid="load-analytics-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Load Analytics
              </Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="denial-summary-cards">
                <Card className="border-red-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Total Denied</p>
                    <p className="text-2xl font-bold text-red-600">{denialAnalytics.summary.total_denied_claims}</p>
                  </CardContent>
                </Card>
                <Card className="border-red-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">$ Denied</p>
                    <p className="text-2xl font-bold text-red-600">${denialAnalytics.summary.total_denied_amount.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Resubmissions</p>
                    <p className="text-2xl font-bold text-amber-600">{denialAnalytics.summary.total_resubmissions}</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Resubmission Rate</p>
                    <p className="text-2xl font-bold text-blue-600">{denialAnalytics.summary.resubmission_rate}%</p>
                  </CardContent>
                </Card>
                <Card className="border-green-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500">Recovery Rate</p>
                    <p className="text-2xl font-bold text-green-600">{denialAnalytics.summary.recovery_rate}%</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Denial Reasons Breakdown */}
                <Card data-testid="denial-reasons-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-red-500" /> Denial Reasons
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {denialAnalytics.denial_reasons.length === 0 ? (
                      <p className="text-sm text-gray-400 py-4 text-center">No denials recorded yet</p>
                    ) : (
                      <div className="space-y-3">
                        {denialAnalytics.denial_reasons.map((r, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-gray-50" data-testid={`denial-reason-${idx}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 break-words">{r.reason}</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                  <span>{r.count} claim{r.count !== 1 ? 's' : ''}</span>
                                  <span>${r.total_billed.toFixed(2)} billed</span>
                                  <span>{r.unique_clients} client{r.unique_clients !== 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              <Badge className={r.resubmitted > 0 ? 'bg-amber-100 text-amber-700 border-amber-200 border text-xs' : 'bg-gray-100 text-gray-500 border text-xs'}>
                                {r.resubmitted} resubmitted
                              </Badge>
                            </div>
                            {r.recovered > 0 && (
                              <div className="mt-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> {r.recovered} recovered (${r.recovered_amount.toFixed(2)})
                              </div>
                            )}
                            {/* Visual bar */}
                            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-red-400 rounded-full transition-all"
                                style={{ width: `${Math.min(100, (r.count / Math.max(...denialAnalytics.denial_reasons.map(x => x.count), 1)) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Client Impact */}
                <Card data-testid="client-impact-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" /> Client Impact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {denialAnalytics.client_impact.length === 0 ? (
                      <p className="text-sm text-gray-400 py-4 text-center">No client denials recorded</p>
                    ) : (
                      <div className="space-y-2">
                        {denialAnalytics.client_impact.map((cl, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-gray-50" data-testid={`client-impact-${idx}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{cl.patient_name}</p>
                                <p className="text-xs text-gray-400 font-mono">{cl.medicaid_id}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-lg font-bold text-red-600">{cl.denial_count}</span>
                                <p className="text-xs text-gray-500">denial{cl.denial_count !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                              <span className="text-red-600 font-medium">${cl.total_denied.toFixed(2)} denied</span>
                              {cl.resubmitted > 0 && (
                                <span className="text-amber-600">{cl.resubmitted} resubmitted</span>
                              )}
                              {cl.recovered > 0 && (
                                <span className="text-green-600">{cl.recovered} recovered</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {cl.reasons.map((r, ri) => (
                                <Badge key={ri} className="bg-red-50 text-red-600 border-red-200 border text-xs font-normal">{r}</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Resubmission Outcomes */}
              <Card data-testid="resubmission-outcomes-card">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-500" /> Resubmission Outcomes
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={loadDenialAnalytics} className="text-xs h-7" data-testid="refresh-analytics-btn">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {denialAnalytics.resubmission_outcomes.total === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">No resubmissions yet</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gray-700">{denialAnalytics.resubmission_outcomes.total}</p>
                        <p className="text-xs text-gray-500 mt-1">Total Resubmissions</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{denialAnalytics.resubmission_outcomes.paid}</p>
                        <p className="text-xs text-gray-500 mt-1">Recovered (Paid)</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{denialAnalytics.resubmission_outcomes.pending}</p>
                        <p className="text-xs text-gray-500 mt-1">Pending</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{denialAnalytics.resubmission_outcomes.denied_again}</p>
                        <p className="text-xs text-gray-500 mt-1">Denied Again</p>
                      </div>
                      {/* Visual progress bar */}
                      <div className="col-span-4">
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
                          {denialAnalytics.resubmission_outcomes.paid > 0 && (
                            <div className="h-full bg-green-500 transition-all" style={{ width: `${(denialAnalytics.resubmission_outcomes.paid / denialAnalytics.resubmission_outcomes.total) * 100}%` }} />
                          )}
                          {denialAnalytics.resubmission_outcomes.pending > 0 && (
                            <div className="h-full bg-amber-400 transition-all" style={{ width: `${(denialAnalytics.resubmission_outcomes.pending / denialAnalytics.resubmission_outcomes.total) * 100}%` }} />
                          )}
                          {denialAnalytics.resubmission_outcomes.denied_again > 0 && (
                            <div className="h-full bg-red-400 transition-all" style={{ width: `${(denialAnalytics.resubmission_outcomes.denied_again / denialAnalytics.resubmission_outcomes.total) * 100}%` }} />
                          )}
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-gray-400">
                          <span>0%</span>
                          <span>Recovery Progress</span>
                          <span>100%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Generate Claims Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Generate Claims from Sessions</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowGenerateModal(false)}><X className="w-4 h-4" /></Button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Select completed sessions to generate CMS-1500 claims. Authorization numbers from PerformCare CYBER will be automatically linked.
              </p>
              {eligibleSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No eligible sessions found.</p>
                  <p className="text-xs mt-1">Complete sessions to generate claims.</p>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex justify-between items-center">
                    <span className="text-sm text-gray-600">{selectedSessionIds.length} of {eligibleSessions.length} selected</span>
                    <Button variant="link" size="sm" onClick={() => {
                      setSelectedSessionIds(selectedSessionIds.length === eligibleSessions.length ? [] : eligibleSessions.map(s => s.id));
                    }}>
                      {selectedSessionIds.length === eligibleSessions.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto border rounded-lg p-2">
                    {eligibleSessions.map((session) => (
                      <div key={session.id}
                        className={`p-2.5 border rounded-md cursor-pointer transition-colors ${
                          selectedSessionIds.includes(session.id) ? 'bg-teal-50 border-teal-300' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setSelectedSessionIds(prev =>
                            prev.includes(session.id) ? prev.filter(id => id !== session.id) : [...prev, session.id]
                          );
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={selectedSessionIds.includes(session.id)} onChange={() => {}} className="w-4 h-4" />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{session.client_name || session.client_id?.substring(0, 8)}</div>
                            <div className="text-xs text-gray-500">
                              {session.session_date} {session.start_time ? `${session.start_time}–${session.end_time}` : ''} {session.cpt_code ? `| ${session.cpt_code}` : ''}
                              {session.medicaid_number ? ` | Medicaid: ${session.medicaid_number}` : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" size="sm" onClick={() => setShowGenerateModal(false)}>Cancel</Button>
                <Button size="sm" onClick={handleGenerateClaims} disabled={selectedSessionIds.length === 0 || loading} className="bg-teal-600 hover:bg-teal-700">
                  {loading
                    ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Generating...</>
                    : <><FileText className="w-4 h-4 mr-1" />Generate {selectedSessionIds.length} Claim(s)</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Preview/Edit */}
      {viewingClaimId && (
        <div className="fixed inset-0 bg-stone-50 z-50 overflow-y-auto">
          <div className="max-w-5xl mx-auto p-4 pb-20">
            <ClaimPreviewEdit
              claimId={viewingClaimId} token={token} apiCall={apiCall} toast={toast}
              onBack={() => { setViewingClaimId(null); loadDDEClaims(); }}
            />
          </div>
        </div>
      )}

      {/* Submission Proof Modal */}
      {proofClaimId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setProofClaimId(null); setProofData(null); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5 text-indigo-600" /> Submission Proof
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">DDE claim submission evidence</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setProofClaimId(null); setProofData(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              {loadingProof ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : proofData?.length > 0 ? (
                proofData.map((ev, idx) => (
                  <div key={idx} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Evidence ID:</span> <span className="font-mono text-xs">{ev.id}</span></div>
                      <div><span className="text-gray-500">Status:</span> <Badge className={ev.verification_status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}>{ev.verification_status}</Badge></div>
                      <div><span className="text-gray-500">Patient:</span> {ev.patient_name}</div>
                      <div><span className="text-gray-500">Medicaid:</span> <span className="font-mono">{ev.medicaid_id}</span></div>
                      <div><span className="text-gray-500">Total:</span> ${ev.total_charge?.toFixed(2)}</div>
                      <div><span className="text-gray-500">Submitted:</span> {ev.submitted_at ? new Date(ev.submitted_at).toLocaleString() : '—'}</div>
                    </div>
                    <div className="space-y-2">
                      {ev.screenshot_filled && (
                        <div data-testid={`proof-filled-${idx}`}>
                          <h4 className="text-sm font-medium text-gray-700">Filled Form (Before Submit)</h4>
                          <img
                            src={`${API_BASE}/api/njmmis/live/claims/dde/${proofClaimId}/proof-screenshot/filled?token=${token}`}
                            alt="Filled CMS-1500 form"
                            className="w-full border rounded-lg shadow-sm"
                            onError={(e) => { e.target.closest('[data-testid]').innerHTML = '<p class="text-sm text-amber-500 py-3 text-center border border-dashed border-amber-300 rounded-lg bg-amber-50">Screenshot unavailable — captured before persistent storage was enabled. Future submissions will retain proof images.</p>'; }}
                          />
                        </div>
                      )}
                      {ev.screenshot_submitted && (
                        <div data-testid={`proof-submitted-${idx}`}>
                          <h4 className="text-sm font-medium text-gray-700 mt-3">Post-Submission Confirmation</h4>
                          <img
                            src={`${API_BASE}/api/njmmis/live/claims/dde/${proofClaimId}/proof-screenshot/submitted?token=${token}`}
                            alt="Submission confirmation"
                            className="w-full border rounded-lg shadow-sm"
                            onError={(e) => { e.target.closest('[data-testid]').innerHTML = '<p class="text-sm text-amber-500 py-3 text-center border border-dashed border-amber-300 rounded-lg bg-amber-50">Screenshot unavailable — captured before persistent storage was enabled. Future submissions will retain proof images.</p>'; }}
                          />
                        </div>
                      )}
                      {!ev.screenshot_filled && !ev.screenshot_submitted && (
                        <p className="text-sm text-gray-400 py-4 text-center">No screenshots captured for this submission.</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Camera className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No submission proof available for this claim.</p>
                  <p className="text-xs mt-1">Proof is captured automatically during DDE submission.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resubmit Claim Modal */}
      {resubmitClaim && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResubmitClaim(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2" data-testid="resubmit-modal-title">
                  <RotateCcw className="w-5 h-5 text-amber-600" /> Resubmit Claim
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {resubmitClaim.patient_first_name} {resubmitClaim.patient_last_name} — Medicaid: {resubmitClaim.medicaid_id}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setResubmitClaim(null)} data-testid="resubmit-modal-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              {/* Original Denial Info */}
              {(resubmitClaim.denial_reason || resubmitClaim.error_codes?.length > 0) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="resubmit-denial-info">
                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-1 mb-1">
                    <XCircle className="w-4 h-4" /> Denial Reason
                  </h4>
                  {resubmitClaim.denial_reason && (
                    <p className="text-sm text-red-700">{resubmitClaim.denial_reason}</p>
                  )}
                  {resubmitClaim.error_codes?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {resubmitClaim.error_codes.map((code, i) => (
                        <Badge key={i} className="bg-red-100 text-red-700 border-red-300 border text-xs">{code}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Prior Authorization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prior Authorization #</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  value={resubmitForm.prior_auth_number || ''}
                  onChange={e => setResubmitForm(prev => ({ ...prev, prior_auth_number: e.target.value }))}
                  placeholder="Enter new or updated auth number"
                  data-testid="resubmit-auth-input"
                />
              </div>

              {/* Diagnosis Codes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis Codes (comma-separated)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono"
                  value={resubmitForm.diagnosis_codes || ''}
                  onChange={e => setResubmitForm(prev => ({ ...prev, diagnosis_codes: e.target.value }))}
                  placeholder="F901, F913"
                  data-testid="resubmit-dx-input"
                />
              </div>

              {/* Service Lines */}
              {resubmitForm.service_lines?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Lines</label>
                  <div className="space-y-2">
                    {resubmitForm.service_lines.map((sl, idx) => (
                      <div key={idx} className="bg-gray-50 border rounded-md p-3" data-testid={`resubmit-service-line-${idx}`}>
                        <div className="text-xs text-gray-500 mb-2 font-mono">
                          {sl.date_from} | {sl.cpt_code} | POS {sl.place_of_service}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Modifiers</label>
                            <input
                              type="text"
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                              value={sl.modifiers}
                              onChange={e => {
                                const updated = [...resubmitForm.service_lines];
                                updated[idx] = { ...updated[idx], modifiers: e.target.value };
                                setResubmitForm(prev => ({ ...prev, service_lines: updated }));
                              }}
                              data-testid={`resubmit-sl-modifiers-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Units</label>
                            <input
                              type="number"
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                              value={sl.units}
                              onChange={e => {
                                const updated = [...resubmitForm.service_lines];
                                updated[idx] = { ...updated[idx], units: e.target.value };
                                setResubmitForm(prev => ({ ...prev, service_lines: updated }));
                              }}
                              data-testid={`resubmit-sl-units-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Charges ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                              value={sl.charges}
                              onChange={e => {
                                const updated = [...resubmitForm.service_lines];
                                updated[idx] = { ...updated[idx], charges: e.target.value };
                                setResubmitForm(prev => ({ ...prev, service_lines: updated }));
                              }}
                              data-testid={`resubmit-sl-charges-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resubmission Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Resubmission</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  rows={2}
                  value={resubmitForm.reason || ''}
                  onChange={e => setResubmitForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Updated authorization, corrected diagnosis codes..."
                  data-testid="resubmit-reason-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setResubmitClaim(null)} data-testid="resubmit-cancel-btn">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleResubmit}
                  disabled={resubmitting}
                  data-testid="resubmit-confirm-btn"
                >
                  {resubmitting
                    ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                    : <><RotateCcw className="w-3 h-3 mr-1" /> Create Corrected Claim</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DDE Progress Tracker - shows during active submissions */}
      {submittingClaimId && (
        <DDEProgressTracker
          claimId={submittingClaimId}
          token={token}
          onComplete={() => loadDDEClaims()}
        />
      )}

      {/* Status History Modal */}
      {statusHistoryClaimId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setStatusHistoryClaimId(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h3 className="font-semibold text-sm">Claim Status History</h3>
              <button onClick={() => setStatusHistoryClaimId(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-4 space-y-3">
              {statusHistory.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No status checks recorded yet</p>
              ) : (
                statusHistory.map((entry, i) => (
                  <div key={i} className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-semibold text-xs px-2 py-0.5 rounded-full ${
                        entry.status === 'paid' ? 'bg-green-100 text-green-800' :
                        entry.status === 'denied' ? 'bg-red-100 text-red-800' :
                        entry.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-700'
                      }`} data-testid={`history-status-${i}`}>
                        {entry.status}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(entry.checked_at)}</span>
                    </div>
                    {entry.paid_amount && <p className="text-green-700 text-xs">Paid: ${entry.paid_amount.toFixed(2)}</p>}
                    {entry.paid_date && <p className="text-xs text-gray-500">Payment date: {entry.paid_date}</p>}
                    {entry.denial_reason && <p className="text-red-600 text-xs">Reason: {entry.denial_reason}</p>}
                    <p className="text-xs text-gray-400 mt-1">Checked by: {entry.checked_by === 'system_batch' ? 'Automated' : entry.checked_by === 'automated_scheduler' ? 'Scheduled' : 'Manual'}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating NJMMIS Progress Tracker */}
      <FloatingNJMMISProgress token={token} />
    </div>
  );
};

export default NJMMISClaimsManagement;
